/**
 * Wrapper around `fetch` for BFF-bound calls.
 *
 * - Always sends cookies (`credentials: 'include'`).
 * - On state-changing methods (POST/PUT/PATCH/DELETE), reads the BFF-issued
 *   `vera.csrf` cookie and echoes its value in the `X-CSRF-Token` header.
 *   The BFF rejects the request with 403 if the header is missing or
 *   doesn't match. See `packages/bff/src/csrf.ts`.
 * - GET/HEAD/OPTIONS go through unchanged (CSRF token not required).
 * - Notices a 401 and hands it to one registered teardown handler, so an
 *   expired session stops the app instead of leaving every page rendering
 *   whatever it already had in cache. See `setUnauthorizedHandler`.
 */

const CSRF_COOKIE_NAME = 'vera.csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Paths whose 401 must NOT tear the app down.
 *
 * The three auth paths are the way back IN, so reacting to their 401 by
 * redirecting to them again is a loop. `/api/form/*` is the public form portal,
 * whose recipients are not Entra users at all: its 401 means "re-open your form
 * link", and sending those people to a Vera sign-in would strand them. (The
 * portal uses its own fetch rather than this one, so this is belt and braces.)
 */
const TEARDOWN_EXEMPT = ['/auth/login', '/auth/logout', '/auth/callback', '/api/form/'];

type UnauthorizedHandler = (reason?: string) => void;

let onUnauthorized: UnauthorizedHandler | null = null;
let alreadyToldOnce = false;

/**
 * Register what happens when the BFF says the session is gone. AuthProvider
 * owns the teardown itself (clear the user, drop the cached Dataverse token,
 * wipe the persisted query cache, redirect to a re-login that prompts); this
 * module only owns noticing.
 *
 * Fires AT MOST ONCE per registration. A window-focus refetch can put twenty
 * requests in flight at the same instant and every one of them comes back 401,
 * so without the latch the user gets twenty teardowns and a redirect race.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
  alreadyToldOnce = false;
}

function pathOf(input: RequestInfo | URL): string {
  try {
    const raw =
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(raw, base).pathname;
  } catch {
    return '';
  }
}

async function reportUnauthorized(path: string, response: Response): Promise<void> {
  if (alreadyToldOnce || !onUnauthorized) return;
  if (TEARDOWN_EXEMPT.some((exempt) => path.startsWith(exempt))) return;
  alreadyToldOnce = true;

  // `reason` is 'idle' or 'absolute' when the BFF's session lifetime guard
  // ended it, and absent for an ordinary "no session" 401. Clone so the caller
  // still gets an unread body.
  let reason: string | undefined;
  try {
    const body = (await response.clone().json()) as { reason?: unknown };
    if (typeof body?.reason === 'string') reason = body.reason;
  } catch {
    /* not JSON, or already consumed - the teardown does not depend on it */
  }

  onUnauthorized(reason);
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${name}=`;
  for (const raw of document.cookie.split(';')) {
    const cookie = raw.trim();
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }
  return undefined;
}

export async function bffFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE_NAME);
    if (token && !headers.has(CSRF_HEADER_NAME)) {
      headers.set(CSRF_HEADER_NAME, token);
    }
  }

  const response = await fetch(input, {
    ...init,
    headers,
    credentials: init.credentials ?? 'include',
  });

  if (response.status === 401) {
    // Deliberately not awaited: the caller's response must not wait on the
    // teardown, and the teardown does not need the caller.
    void reportUnauthorized(pathOf(input), response);
  }

  return response;
}
